import axiosInstance from "@/utils/axiosInstance";

export type WorkflowLifecycleState =
  | "draft"
  | "validated"
  | "published"
  | "superseded";

export interface ApprovedWorkflowNode {
  node_code: string;
  stage_code: string;
  name: string;
  is_start: boolean;
  is_terminal: boolean;
}

export interface ApprovedWorkflowTransition {
  from_status: string;
  to_status: string;
  action_code: string;
  stage?: string | null;
  decision_state?: string | null;
  allowed_roles: string[];
  requires_assignment: boolean;
  requires_student_action: boolean;
  is_terminal: boolean;
  owner_role: string;
  override_roles: string[];
}

export interface WorkflowBusinessRules {
  funding: {
    funding_type: "single_year" | "multi_year";
    number_of_years: number;
  };
  renewal: { renewal_rule: string };
  mentoring: { required: boolean; completion_criteria: string[] };
  interview: { required: boolean };
  approvals: {
    management_required: boolean;
    client_governance_required: boolean;
  };
  eligibility: Record<string, unknown>;
  evidence: Record<string, unknown>;
  scoring: Record<string, unknown>;
  budget: Record<string, unknown>;
}

export interface WorkflowApprovedComponents {
  nodes: ApprovedWorkflowNode[];
  transitions: ApprovedWorkflowTransition[];
  business_rule_defaults: WorkflowBusinessRules;
}

export interface TenantWorkflowNode {
  id: number;
  node_code: string;
  stage_code: string;
  display_name: string;
  display_order: number;
  is_start: boolean;
  is_terminal: boolean;
}

export interface TenantWorkflowTransition extends ApprovedWorkflowTransition {
  id: number;
}

export interface TenantWorkflowVersion {
  id: number;
  workflow_definition_id: number;
  version: number;
  lifecycle_state: WorkflowLifecycleState;
  seed_workflow_template_id?: number | null;
  source_scholarship_standard_id?: number | null;
  business_rules: WorkflowBusinessRules;
  published_snapshot?: Record<string, unknown> | null;
  snapshot_hash?: string | null;
  validated_at?: string | null;
  published_at?: string | null;
  superseded_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  nodes: TenantWorkflowNode[];
  transitions: TenantWorkflowTransition[];
}

export interface TenantWorkflowDefinition {
  id: number;
  tenant_id: number;
  program_code: string;
  definition_key: string;
  name: string;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  versions: TenantWorkflowVersion[];
}

export interface WorkflowDraftPayload {
  nodes: Array<{
    node_code: string;
    display_name?: string | null;
    display_order: number;
  }>;
  transitions: Array<{
    from_status: string;
    to_status: string;
    action_code: string;
  }>;
  business_rules: WorkflowBusinessRules;
}

export async function listTenantWorkflows(): Promise<TenantWorkflowDefinition[]> {
  const response = await axiosInstance.get<TenantWorkflowDefinition[]>(
    "/admin/workflow-definitions"
  );
  return response.data;
}

export async function getTenantWorkflow(
  definitionId: number
): Promise<TenantWorkflowDefinition> {
  const response = await axiosInstance.get<TenantWorkflowDefinition>(
    `/admin/workflow-definitions/${definitionId}`
  );
  return response.data;
}

export async function getApprovedWorkflowComponents(): Promise<WorkflowApprovedComponents> {
  const response = await axiosInstance.get<WorkflowApprovedComponents>(
    "/admin/workflow-definitions/approved-components"
  );
  return response.data;
}

export async function createTenantWorkflow(payload: {
  program_code: string;
  name: string;
  description?: string | null;
  seed_workflow_template_id?: number | null;
  source_scholarship_standard_id?: number | null;
}): Promise<TenantWorkflowDefinition> {
  const response = await axiosInstance.post<TenantWorkflowDefinition>(
    "/admin/workflow-definitions",
    payload
  );
  return response.data;
}

export async function updateTenantWorkflowDraft(
  definitionId: number,
  versionId: number,
  payload: WorkflowDraftPayload
): Promise<TenantWorkflowVersion> {
  const response = await axiosInstance.put<TenantWorkflowVersion>(
    `/admin/workflow-definitions/${definitionId}/versions/${versionId}`,
    payload
  );
  return response.data;
}

export async function deleteTenantWorkflowDraft(
  definitionId: number,
  versionId: number
): Promise<void> {
  await axiosInstance.delete(
    `/admin/workflow-definitions/${definitionId}/versions/${versionId}`
  );
}

export async function validateTenantWorkflow(
  definitionId: number,
  versionId: number
): Promise<TenantWorkflowVersion> {
  const response = await axiosInstance.post<TenantWorkflowVersion>(
    `/admin/workflow-definitions/${definitionId}/versions/${versionId}/validate`
  );
  return response.data;
}

export async function publishTenantWorkflow(
  definitionId: number,
  versionId: number
): Promise<TenantWorkflowVersion> {
  const response = await axiosInstance.post<TenantWorkflowVersion>(
    `/admin/workflow-definitions/${definitionId}/versions/${versionId}/publish`
  );
  return response.data;
}

export async function createTenantWorkflowVersion(
  definitionId: number,
  sourceVersionId: number
): Promise<TenantWorkflowVersion> {
  const response = await axiosInstance.post<TenantWorkflowVersion>(
    `/admin/workflow-definitions/${definitionId}/versions`,
    { source_version_id: sourceVersionId }
  );
  return response.data;
}
